import React, { useState, useCallback } from 'react';
import { Modal, ModalContent, ModalBody, Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import { ResponsiveModal, Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';

export interface ContentLink {
  category: string;
  url: string;
}

interface AccountContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  modelId: number;
  mediaUrls?: string[];
  contentLinks?: ContentLink[];
}

const AccountContentModal: React.FC<AccountContentModalProps> = ({
  isOpen,
  onClose,
  modelId,
  mediaUrls = [],
  contentLinks = [],
}) => {
  const { t } = useTranslations();
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleImageClick = (url: string) => {
    setFullscreenImage(url);
  };

  const handleCloseFullscreen = () => {
    setFullscreenImage(null);
  };

  const handleNextImage = () => {
    if (!fullscreenImage || mediaUrls.length === 0) return;
    const currentIndex = mediaUrls.indexOf(fullscreenImage);
    const nextIndex = (currentIndex + 1) % mediaUrls.length;
    setFullscreenImage(mediaUrls[nextIndex]);
  };

  const handlePrevImage = () => {
    if (!fullscreenImage || mediaUrls.length === 0) return;
    const currentIndex = mediaUrls.indexOf(fullscreenImage);
    const prevIndex = currentIndex === 0 ? mediaUrls.length - 1 : currentIndex - 1;
    setFullscreenImage(mediaUrls[prevIndex]);
  };

  const handleCopyLink = useCallback(async (url: string, index: number) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-white rounded-4 shadow-[0px_0px_11px_0px_rgba(0,0,0,0.1)] border border-light',
        closeButton: 'top-2 right-2 text-auth-muted hover:bg-transparent',
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            {/* Header */}
            <div className="border-b border-auth-surface-hover px-4 py-5">
              <div className="flex items-center gap-2">
                <Typography variant="body2" className="font-medium text-auth-ink">
                  ON{modelId}
                </Typography>
                <span className="text-base font-medium text-auth-muted">
                  {t('partner.revshare.accounts.contentModal.content')}
                </span>
              </div>
            </div>

            <ModalBody className="p-4 flex flex-col gap-6">
              {/* Превью контента Section */}
              {mediaUrls.length > 0 && (
                <div className="flex flex-col gap-2">
                  <Typography variant="body2" className="text-auth-ink">
                    {t('partner.revshare.accounts.contentModal.previewTitle')}
                  </Typography>
                  <div className="flex flex-wrap gap-4">
                    {mediaUrls.map((url, index) => (
                      <div
                        key={index}
                        className="size-[6.25rem] rounded-2 overflow-hidden bg-auth-surface cursor-pointer relative group shrink-0"
                        onClick={() => handleImageClick(url)}
                      >
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                          <Icon icon="solar:eye-bold" width={24} className="text-white" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ссылки на контент Section */}
              {contentLinks.length > 0 && (
                <div className="flex flex-col gap-2">
                  <Typography variant="body2" className="text-auth-ink">
                    {t('partner.revshare.accounts.contentModal.linksTitle')}
                  </Typography>
                  <div className="bg-white border border-tin rounded-5 overflow-hidden p-4 flex flex-col gap-2">
                    {/* Table Header */}
                    <div className="flex items-center gap-6 bg-auth-surface border border-tin rounded-2.5 px-2 py-3 shadow-[0px_1px_0px_0px_rgba(0,0,0,0.05)]">
                      <div className="w-[12.5rem] pr-6">
                        <Typography variant="body2" className="font-medium text-auth-muted">
                          {t('partner.revshare.accounts.contentModal.category')}
                        </Typography>
                      </div>
                      <div className="flex-1">
                        <Typography variant="body2" className="font-medium text-auth-muted">
                          {t('partner.revshare.accounts.contentModal.contentLink')}
                        </Typography>
                      </div>
                    </div>

                    {/* Table Rows */}
                    {contentLinks.map((link, index) => (
                      <div key={index} className="flex items-center gap-6 px-2 py-3">
                        <div className="w-[12.5rem]">
                          <Typography variant="body2" className="text-auth-ink">
                            {link.category}
                          </Typography>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="bg-husky rounded-5 px-2 py-0.5 flex items-center gap-1">
                            <span className="text-sm text-partner truncate flex-1">{link.url}</span>
                            <button
                              onClick={() => handleCopyLink(link.url, index)}
                              className="shrink-0 p-0.5 hover:opacity-70 transition-opacity"
                              title={copiedIndex === index ? t('common.copied') : t('common.copy')}
                            >
                              <Icon
                                icon={copiedIndex === index ? "lucide:check" : "solar:copy-linear"}
                                width={16}
                                className="text-partner"
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div className="flex justify-end">
                <Button
                  color="primary"
                  size="sm"
                  onPress={onClose}
                  className="bg-partner text-white text-xs h-8 px-3 rounded-2"
                >
                  {t('partner.revshare.accounts.contentModal.close')}
                </Button>
              </div>
            </ModalBody>

            {/* Fullscreen Image Viewer */}
            {fullscreenImage && (
              <div
                className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center"
                onClick={handleCloseFullscreen}
              >
                {/* Close Button */}
                <button
                  className="absolute top-0 right-0 z-10 w-12 h-12 flex items-center justify-center"
                  onClick={handleCloseFullscreen}
                >
                  <Icon icon="lucide:x" width={24} className="text-white" />
                </button>

                {/* Previous Button */}
                <button
                  className="absolute left-4 z-10 w-8 h-8 flex items-center justify-center rounded-2 bg-partner shadow-[0px_10px_15px_-3px_rgba(0,111,238,0.4),0px_4px_6px_-2px_rgba(0,0,0,0.05)] hover:bg-[#005FCC] transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevImage();
                  }}
                >
                  <Icon icon="solar:alt-arrow-left-linear" width={16} className="text-white" />
                </button>

                {/* Next Button */}
                <button
                  className="absolute right-4 z-10 w-8 h-8 flex items-center justify-center rounded-2 bg-partner shadow-[0px_10px_15px_-3px_rgba(0,111,238,0.4),0px_4px_6px_-2px_rgba(0,0,0,0.05)] hover:bg-[#005FCC] transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextImage();
                  }}
                >
                  <Icon icon="solar:alt-arrow-right-linear" width={16} className="text-white" />
                </button>

                {/* Image */}
                <img
                  src={fullscreenImage}
                  alt="Fullscreen preview"
                  className="max-w-[90vw] max-h-[90vh] object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </>
        )}
      </ModalContent>
    </ResponsiveModal>
  );
};

export default AccountContentModal;
